import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const TOKEN_ITEM_BASE = '!item';
const TOKEN_BOM_BASE = '!bom';
const TOKEN_GUIDE_BASE = '!guide';
const TOKEN_BASES = [TOKEN_ITEM_BASE, TOKEN_BOM_BASE, TOKEN_GUIDE_BASE];
const MAX_OPTIONS = 60;
const BOM_FILTER_TYPES = ['category', 'module', 'version'];

function normalizeKey(value) {
  return (value || '').toString().trim().toLowerCase();
}

function sanitizeText(value) {
  return (value || '').toString();
}

function clampCaret(value, caret) {
  const text = sanitizeText(value);
  if (!Number.isFinite(caret)) return text.length;
  if (caret < 0) return 0;
  if (caret > text.length) return text.length;
  return caret;
}

function findAutocompleteTokenStart(text, caret) {
  let bestIndex = -1;
  let bestToken = '';
  TOKEN_BASES.forEach((token) => {
    const index = text.lastIndexOf(token, caret);
    if (index > bestIndex) {
      bestIndex = index;
      bestToken = token;
    }
  });

  return {
    tokenStart: bestIndex,
    tokenBase: bestToken
  };
}

function getAutocompleteContext(value, caretInput) {
  const text = sanitizeText(value);
  const caret = clampCaret(text, caretInput);
  const tokenInfo = findAutocompleteTokenStart(text, caret);
  const tokenStart = tokenInfo.tokenStart;
  const tokenBase = tokenInfo.tokenBase;
  if (tokenStart < 0) return null;

  const body = text.slice(tokenStart + tokenBase.length, caret);
  if (body.includes('\n') || body.includes('\r')) return null;

  const isItemToken = tokenBase === TOKEN_ITEM_BASE;
  const isBomToken = tokenBase === TOKEN_BOM_BASE;
  const isGuideToken = tokenBase === TOKEN_GUIDE_BASE;

  if (!body) {
    const stage = isGuideToken ? 'guide' : 'bom';
    return {
      tokenType: isGuideToken ? 'guide' : (isBomToken ? 'bom' : 'item'),
      stage,
      query: '',
      replaceStart: tokenStart + tokenBase.length,
      replaceEnd: caret,
      insertPrefix: '['
    };
  }

  if (!body.startsWith('[')) return null;

  const prefixSlice = body.slice(1);

  if (isBomToken || isGuideToken) {
    const closeBracketIndex = prefixSlice.indexOf(']');
    if (closeBracketIndex >= 0) {
      return null;
    }
    if (isBomToken) {
      const commaIndex = prefixSlice.indexOf(',');
      if (commaIndex >= 0) {
        return {
          tokenType: 'bom',
          stage: 'bom-filter',
          bomSlug: prefixSlice.slice(0, commaIndex).trim(),
          query: prefixSlice.slice(commaIndex + 1),
          replaceStart: tokenStart + tokenBase.length + 1 + commaIndex + 1,
          replaceEnd: caret
        };
      }
    }
    return {
      tokenType: isGuideToken ? 'guide' : 'bom',
      stage: isGuideToken ? 'guide' : 'bom',
      query: prefixSlice,
      replaceStart: tokenStart + tokenBase.length + 1,
      replaceEnd: caret,
      insertPrefix: ''
    };
  }

  if (!isItemToken) {
    return null;
  }

  const bomDelimiterIndex = prefixSlice.indexOf('](');
  if (bomDelimiterIndex < 0) {
    if (prefixSlice.includes(']')) return null;
    return {
      tokenType: 'item',
      stage: 'bom',
      query: prefixSlice,
      replaceStart: tokenStart + tokenBase.length + 1,
      replaceEnd: caret,
      insertPrefix: ''
    };
  }

  const bomSlug = prefixSlice.slice(0, bomDelimiterIndex).trim();
  const itemSlice = prefixSlice.slice(bomDelimiterIndex + 2);
  if (itemSlice.includes(')')) return null;

  return {
    tokenType: 'item',
    stage: 'item',
    bomSlug,
    query: itemSlice,
    replaceStart: tokenStart + tokenBase.length + 1 + bomDelimiterIndex + 2,
    replaceEnd: caret
  };
}

function getAutocompleteTitle(context) {
  if (context?.stage === 'guide') {
    return 'Select guide';
  }
  if (context?.stage === 'item') {
    return 'Select item';
  }
  if (context?.stage === 'bom-filter') {
    return 'Select BOM filter';
  }
  return 'Select BOM first';
}

function buildBomOptions(catalog, query) {
  const normalizedQuery = normalizeKey(query);
  const boms = Array.isArray(catalog) ? catalog : [];
  const options = [];

  boms.forEach((bom) => {
    const slugURL = sanitizeText(bom?.slugURL).trim();
    if (!slugURL) return;
    const title = sanitizeText(bom?.title).trim() || slugURL;
    const slugKey = normalizeKey(slugURL);
    const titleKey = normalizeKey(title);

    if (!normalizedQuery || slugKey.includes(normalizedQuery) || titleKey.includes(normalizedQuery)) {
      options.push({
        type: 'bom',
        value: slugURL,
        label: title,
        subtitle: slugURL,
        sortKey: `${titleKey}|${slugKey}`
      });
    }
  });

  return options
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(0, MAX_OPTIONS);
}

function buildItemOptions(catalog, bomSlug, query) {
  const normalizedBomSlug = normalizeKey(bomSlug);
  if (!normalizedBomSlug) return [];

  const boms = Array.isArray(catalog) ? catalog : [];
  const bom = boms.find((entry) => normalizeKey(entry?.slugURL) === normalizedBomSlug);
  if (!bom) return [];

  const normalizedQuery = normalizeKey(query);
  const items = Array.isArray(bom?.items) ? bom.items : [];
  const options = [];

  items.forEach((item) => {
    const identifier = sanitizeText(item?.identifier).trim().toLowerCase();
    if (!identifier) return;

    const name = sanitizeText(item?.name || item?.title).trim();
    const identifierKey = normalizeKey(identifier);
    const nameKey = normalizeKey(name);

    if (!normalizedQuery || identifierKey.includes(normalizedQuery) || nameKey.includes(normalizedQuery)) {
      options.push({
        type: 'item',
        value: identifier,
        label: identifier,
        subtitle: name || '-',
        sortKey: `${identifierKey}|${nameKey}`
      });
    }
  });

  return options
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(0, MAX_OPTIONS);
}

function findBomCatalogEntry(catalog, bomSlug) {
  const normalizedBomSlug = normalizeKey(bomSlug);
  if (!normalizedBomSlug) return null;
  const boms = Array.isArray(catalog) ? catalog : [];
  return boms.find((entry) => normalizeKey(entry?.slugURL) === normalizedBomSlug) || null;
}

function hasExactItemMatch(options, query) {
  const normalizedQuery = normalizeKey(query);
  if (!normalizedQuery) return false;
  return (Array.isArray(options) ? options : []).some((option) => (
    normalizeKey(option?.value) === normalizedQuery
    || normalizeKey(option?.label) === normalizedQuery
    || normalizeKey(option?.subtitle) === normalizedQuery
  ));
}

function collectUniqueBomFilterValues(items, field) {
  const seen = new Set();
  const values = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const rawValues = field === 'version'
      ? (Array.isArray(item?.versions) ? item.versions : [])
      : [item?.[field]];
    rawValues.forEach((rawValue) => {
      const value = sanitizeText(rawValue).trim();
      if (!value) return;
      const key = normalizeKey(value);
      if (seen.has(key)) return;
      seen.add(key);
      values.push(value);
    });
  });
  return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function buildBomFilterOptions(catalog, bomSlug, query) {
  const normalizedBomSlug = normalizeKey(bomSlug);
  if (!normalizedBomSlug) return [];

  const boms = Array.isArray(catalog) ? catalog : [];
  const bom = boms.find((entry) => normalizeKey(entry?.slugURL) === normalizedBomSlug);
  if (!bom) return [];

  const queryText = sanitizeText(query).trim();
  const equalsIndex = queryText.indexOf('=');
  const requestedType = equalsIndex >= 0 ? normalizeKey(queryText.slice(0, equalsIndex)) : '';
  const normalizedQuery = normalizeKey(equalsIndex >= 0 ? queryText.slice(equalsIndex + 1) : queryText);
  const allowedTypes = BOM_FILTER_TYPES.includes(requestedType)
    ? [requestedType]
    : BOM_FILTER_TYPES;
  const items = Array.isArray(bom?.items) ? bom.items : [];
  const options = [];

  allowedTypes.forEach((filterType) => {
    collectUniqueBomFilterValues(items, filterType).forEach((filterValue) => {
      const valueKey = normalizeKey(filterValue);
      if (
        normalizedQuery
        && !valueKey.includes(normalizedQuery)
        && !filterType.includes(normalizedQuery)
      ) {
        return;
      }
      options.push({
        type: 'bom-filter',
        filterType,
        value: filterValue,
        label: `${filterType[0].toUpperCase()}${filterType.slice(1)}: ${filterValue}`,
        subtitle: `${filterType}=${filterValue}`,
        sortKey: `${filterType}|${valueKey}`
      });
    });
  });

  return options
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(0, MAX_OPTIONS);
}

function buildGuideOptions(catalog, query) {
  const normalizedQuery = normalizeKey(query);
  const guides = Array.isArray(catalog) ? catalog : [];
  const options = [];

  guides.forEach((guide) => {
    const slugURL = sanitizeText(guide?.slugURL).trim();
    if (!slugURL) return;
    const title = sanitizeText(guide?.title).trim() || slugURL;
    const slugKey = normalizeKey(slugURL);
    const titleKey = normalizeKey(title);

    if (!normalizedQuery || slugKey.includes(normalizedQuery) || titleKey.includes(normalizedQuery)) {
      options.push({
        type: 'guide',
        value: slugURL,
        label: title,
        subtitle: slugURL,
        sortKey: `${titleKey}|${slugKey}`
      });
    }
  });

  return options
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(0, MAX_OPTIONS);
}

function moveSelection(currentIndex, listLength, delta) {
  if (!listLength) return 0;
  const next = currentIndex + delta;
  if (next < 0) return listLength - 1;
  if (next >= listLength) return 0;
  return next;
}

function setCaretPosition(textarea, position) {
  if (!textarea) return;
  try {
    textarea.focus();
    textarea.setSelectionRange(position, position);
  } catch (e) {
    // no-op
  }
}

const CARET_MIRROR_STYLE_PROPS = [
  'boxSizing',
  'width',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'textTransform',
  'textAlign',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'wordSpacing',
  'tabSize'
];

function parseLineHeightPx(computedStyle) {
  const lineHeightRaw = (computedStyle?.lineHeight || '').toString().trim();
  if (lineHeightRaw && lineHeightRaw !== 'normal' && lineHeightRaw.endsWith('px')) {
    const parsed = Number.parseFloat(lineHeightRaw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  const fontSize = Number.parseFloat((computedStyle?.fontSize || '16').toString());
  if (Number.isFinite(fontSize) && fontSize > 0) return fontSize * 1.2;
  return 20;
}

function getCaretCoordinates(textarea, caretPosition) {
  if (!textarea || typeof window === 'undefined' || typeof document === 'undefined') {
    return { top: 0, left: 0, lineHeight: 20 };
  }

  const computedStyle = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordBreak = 'break-word';
  mirror.style.overflow = 'hidden';
  mirror.style.left = '-9999px';
  mirror.style.top = '0';
  mirror.style.pointerEvents = 'none';

  CARET_MIRROR_STYLE_PROPS.forEach((styleProp) => {
    mirror.style[styleProp] = computedStyle[styleProp];
  });

  const value = sanitizeText(textarea.value);
  const clampedCaret = clampCaret(value, caretPosition);
  const textBeforeCaret = value.slice(0, clampedCaret);
  mirror.textContent = textBeforeCaret;

  if (textBeforeCaret.endsWith('\n')) {
    mirror.textContent += '\u200b';
  }

  const marker = document.createElement('span');
  marker.textContent = value.slice(clampedCaret) || '\u200b';
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const top = marker.offsetTop;
  const left = marker.offsetLeft;
  document.body.removeChild(mirror);

  return {
    top,
    left,
    lineHeight: parseLineHeightPx(computedStyle)
  };
}

function BomMarkdownTextarea({
  id,
  value,
  onValueChange,
  onBlur,
  onFocus,
  onKeyDown,
  onClick,
  onSelect,
  className,
  style,
  rows,
  placeholder,
  disabled,
  autoComplete = 'off',
  spellCheck,
  ariaLabel,
  wrapperClassName,
  wrapperStyle,
  bomAutocompleteCatalog,
  guideAutocompleteCatalog,
  onCreateBomItem,
  textareaRef,
  children
}) {
  const internalRef = useRef(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const datalistIdRef = useRef(`bom-create-${Math.random().toString(36).slice(2, 10)}`);
  const [caretPos, setCaretPos] = useState(() => sanitizeText(value).length);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 320 });
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    quantity: '',
    category: '',
    module: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const bomCatalog = useMemo(() => {
    return Array.isArray(bomAutocompleteCatalog) ? bomAutocompleteCatalog : [];
  }, [bomAutocompleteCatalog]);
  const guideCatalog = useMemo(() => {
    return Array.isArray(guideAutocompleteCatalog) ? guideAutocompleteCatalog : [];
  }, [guideAutocompleteCatalog]);

  const autocompleteState = useMemo(() => {
    const context = getAutocompleteContext(value, caretPos);
    if (!isFocused && !createFormOpen) return { open: false, options: [], context: null, canCreateItem: false, showCreateFromQuery: false };
    if (!context) return { open: false, options: [], context: null };

    let options = [];
    if (context.stage === 'item') {
      options = buildItemOptions(bomCatalog, context.bomSlug, context.query);
    } else if (context.stage === 'bom-filter') {
      options = buildBomFilterOptions(bomCatalog, context.bomSlug, context.query);
    } else if (context.stage === 'guide') {
      options = buildGuideOptions(guideCatalog, context.query);
    } else {
      options = buildBomOptions(bomCatalog, context.query);
    }

    const canCreateItem = context.stage === 'item'
      && typeof onCreateBomItem === 'function'
      && !!findBomCatalogEntry(bomCatalog, context.bomSlug);
    const showCreateFromQuery = canCreateItem
      && !!sanitizeText(context.query).trim()
      && !hasExactItemMatch(options, context.query);

    if (!options.length) {
      if (!canCreateItem) {
        return { open: false, options: [], context: null, canCreateItem: false, showCreateFromQuery: false };
      }
      return {
        open: true,
        options,
        context,
        canCreateItem,
        showCreateFromQuery
      };
    }

    return {
      open: true,
      options,
      context,
      canCreateItem,
      showCreateFromQuery
    };
  }, [value, caretPos, bomCatalog, guideCatalog, isFocused, createFormOpen, onCreateBomItem]);

  const createFormSuggestions = useMemo(() => {
    const bom = findBomCatalogEntry(bomCatalog, autocompleteState.context?.bomSlug);
    const items = Array.isArray(bom?.items) ? bom.items : [];
    return {
      category: collectUniqueBomFilterValues(items, 'category').slice(0, MAX_OPTIONS),
      module: collectUniqueBomFilterValues(items, 'module').slice(0, MAX_OPTIONS)
    };
  }, [bomCatalog, autocompleteState.context?.bomSlug]);

  const categoryDatalistId = `${datalistIdRef.current}-category`;
  const moduleDatalistId = `${datalistIdRef.current}-module`;

  useEffect(() => {
    setActiveIndex(0);
  }, [autocompleteState.context?.stage, autocompleteState.context?.replaceStart, autocompleteState.options.length]);

  useEffect(() => {
    if (autocompleteState.context?.stage !== 'item') {
      setCreateFormOpen(false);
      setCreateError('');
    }
  }, [autocompleteState.context?.stage, autocompleteState.context?.bomSlug]);

  const updateMenuPosition = useCallback(() => {
    if (!autocompleteState.open) return;
    const textarea = internalRef.current;
    const wrapper = wrapperRef.current;
    if (!textarea || !wrapper) return;

    const caret = clampCaret(value, caretPos);
    const caretCoords = getCaretCoordinates(textarea, caret);
    const horizontalPadding = 8;
    const desiredWidth = Math.min(360, Math.max(240, textarea.clientWidth * 0.55));
    const maxLeft = Math.max(horizontalPadding, textarea.clientWidth - desiredWidth - horizontalPadding);
    const rawLeft = textarea.offsetLeft + caretCoords.left - textarea.scrollLeft;
    const left = Math.max(horizontalPadding, Math.min(rawLeft, maxLeft));
    const top = Math.max(
      0,
      textarea.offsetTop + caretCoords.top - textarea.scrollTop + caretCoords.lineHeight + 6
    );

    setMenuPosition({
      top,
      left,
      width: desiredWidth
    });
  }, [autocompleteState.open, value, caretPos]);

  useEffect(() => {
    updateMenuPosition();
  }, [updateMenuPosition, autocompleteState.open, autocompleteState.options.length, autocompleteState.context?.stage]);

  useEffect(() => {
    if (!autocompleteState.open) return undefined;
    const textarea = internalRef.current;
    if (!textarea) return undefined;

    const handleScroll = () => {
      updateMenuPosition();
    };

    textarea.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      textarea.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [autocompleteState.open, updateMenuPosition]);

  useEffect(() => {
    if (!autocompleteState.open) return;
    const menu = menuRef.current;
    if (!menu) return;
    const activeOption = menu.querySelector('.bom-autocomplete-option.is-active');
    if (!activeOption) return;

    const optionTop = activeOption.offsetTop;
    const optionBottom = optionTop + activeOption.offsetHeight;
    const menuTop = menu.scrollTop;
    const menuBottom = menuTop + menu.clientHeight;

    if (optionTop < menuTop) {
      menu.scrollTop = optionTop;
      return;
    }

    if (optionBottom > menuBottom) {
      menu.scrollTop = optionBottom - menu.clientHeight;
    }
  }, [autocompleteState.open, autocompleteState.options.length, activeIndex]);

  const handleSelectionCapture = (event) => {
    setCaretPos(event?.target?.selectionStart ?? 0);
  };

  const emitValueChange = (nextValue) => {
    if (typeof onValueChange === 'function') {
      onValueChange(nextValue);
    }
  };

  const handleTextareaChange = (event) => {
    emitValueChange(event.target.value);
    setCaretPos(event.target.selectionStart ?? 0);
  };

  const selectBomOption = (option) => {
    const context = autocompleteState.context;
    if (!context || context.stage !== 'bom') return;

    const text = sanitizeText(value);
    const slug = sanitizeText(option?.value).trim();
    if (!slug) return;
    const insertPrefix = context.insertPrefix || '';
    const insertion = context.tokenType === 'item'
      ? `${insertPrefix}${slug}](`
      : `${insertPrefix}${slug}]`;
    const nextValue = `${text.slice(0, context.replaceStart)}${insertion}${text.slice(context.replaceEnd)}`;
    const nextCaret = context.replaceStart + insertion.length;

    emitValueChange(nextValue);
    setCaretPos(nextCaret);

    requestAnimationFrame(() => {
      setCaretPosition(internalRef.current, nextCaret);
    });
  };

  const selectGuideOption = (option) => {
    const context = autocompleteState.context;
    if (!context || context.stage !== 'guide') return;

    const text = sanitizeText(value);
    const slug = sanitizeText(option?.value).trim();
    if (!slug) return;
    const insertPrefix = context.insertPrefix || '';
    const insertion = `${insertPrefix}${slug}]`;
    const nextValue = `${text.slice(0, context.replaceStart)}${insertion}${text.slice(context.replaceEnd)}`;
    const nextCaret = context.replaceStart + insertion.length;

    emitValueChange(nextValue);
    setCaretPos(nextCaret);

    requestAnimationFrame(() => {
      setCaretPosition(internalRef.current, nextCaret);
    });
  };

  const insertItemIdentifier = (identifierInput, contextInput = autocompleteState.context) => {
    const context = contextInput;
    if (!context || context.stage !== 'item') return;

    const text = sanitizeText(value);
    const identifier = sanitizeText(identifierInput).trim().toLowerCase();
    if (!identifier) return;

    const nextValue = `${text.slice(0, context.replaceStart)}${identifier})${text.slice(context.replaceEnd)}`;
    const nextCaret = context.replaceStart + identifier.length + 1;

    emitValueChange(nextValue);
    setCaretPos(nextCaret);

    requestAnimationFrame(() => {
      setCaretPosition(internalRef.current, nextCaret);
    });
  };

  const selectItemOption = (option) => {
    insertItemIdentifier(option?.value);
  };

  const selectBomFilterOption = (option) => {
    const context = autocompleteState.context;
    if (!context || context.stage !== 'bom-filter') return;

    const text = sanitizeText(value);
    const filterType = normalizeKey(option?.filterType);
    const filterValue = sanitizeText(option?.value).trim();
    if (!BOM_FILTER_TYPES.includes(filterType) || !filterValue) return;

    const insertion = ` ${filterType}=${filterValue}]`;
    const nextValue = `${text.slice(0, context.replaceStart)}${insertion}${text.slice(context.replaceEnd)}`;
    const nextCaret = context.replaceStart + insertion.length;

    emitValueChange(nextValue);
    setCaretPos(nextCaret);

    requestAnimationFrame(() => {
      setCaretPosition(internalRef.current, nextCaret);
    });
  };

  const commitActiveOption = () => {
    if (!autocompleteState.open) return false;
    const option = autocompleteState.options[activeIndex] || autocompleteState.options[0];
    if (!option) return false;

    if (autocompleteState.context?.stage === 'bom') {
      selectBomOption(option);
      return true;
    }
    if (autocompleteState.context?.stage === 'guide') {
      selectGuideOption(option);
      return true;
    }
    if (autocompleteState.context?.stage === 'item') {
      selectItemOption(option);
      return true;
    }
    if (autocompleteState.context?.stage === 'bom-filter') {
      selectBomFilterOption(option);
      return true;
    }
    return false;
  };

  const handleTextareaKeyDown = (event) => {
    if (autocompleteState.open && !createFormOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => moveSelection(current, autocompleteState.options.length, 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => moveSelection(current, autocompleteState.options.length, -1));
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        if (commitActiveOption()) {
          event.preventDefault();
        }
      }
    }

    if (typeof onKeyDown === 'function') {
      onKeyDown(event);
    }
  };

  const openCreateForm = (prefillName = '') => {
    const nextName = sanitizeText(prefillName).trim();
    setCreateForm({
      name: nextName,
      description: '',
      quantity: '',
      category: '',
      module: ''
    });
    setCreateError('');
    setCreateFormOpen(true);
  };

  const closeCreateForm = () => {
    setCreateFormOpen(false);
    setCreateError('');
    requestAnimationFrame(() => {
      internalRef.current?.focus();
    });
  };

  const updateCreateFormField = (field, fieldValue) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: sanitizeText(fieldValue)
    }));
  };

  const submitCreateForm = async (event) => {
    event.preventDefault();
    if (!autocompleteState.canCreateItem || typeof onCreateBomItem !== 'function') return;
    const context = autocompleteState.context;
    if (!context || context.stage !== 'item') return;

    const name = sanitizeText(createForm.name).trim();
    if (!name) {
      setCreateError('Item name is required.');
      return;
    }

    setCreateLoading(true);
    setCreateError('');
    try {
      const response = await onCreateBomItem({
        bomSlug: context.bomSlug,
        item: {
          name,
          description: createForm.description,
          quantity: createForm.quantity,
          category: createForm.category,
          module: createForm.module
        }
      });
      if (!response?.ok) {
        setCreateError(response?.message || 'Unable to create item.');
        return;
      }
      const identifier = response?.referenceItem?.identifier || response?.item?.identifier;
      if (!identifier) {
        setCreateError('Item was created, but no identifier was returned.');
        return;
      }
      insertItemIdentifier(identifier, context);
      setCreateFormOpen(false);
    } catch (err) {
      setCreateError(err?.message || 'Unable to create item.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleWrapperBlur = (event) => {
    if (!createFormOpen) return;
    if (event?.target === internalRef.current) return;
    const nextTarget = event?.relatedTarget || null;
    if (nextTarget && wrapperRef.current?.contains(nextTarget)) {
      return;
    }
    setIsFocused(false);
    setCreateFormOpen(false);
    if (typeof onBlur === 'function') {
      onBlur({ target: { value: sanitizeText(value) } });
    }
  };

  const assignTextareaRef = (node) => {
    internalRef.current = node;
    if (textareaRef && typeof textareaRef === 'object') {
      textareaRef.current = node;
    } else if (typeof textareaRef === 'function') {
      textareaRef(node);
    }
  };

  return (
    <div ref={wrapperRef} className={`bom-markdown-textarea ${wrapperClassName || ''}`} style={wrapperStyle} onBlur={handleWrapperBlur}>
      <textarea
        id={id}
        ref={assignTextareaRef}
        className={className}
        style={style}
        rows={rows}
        value={sanitizeText(value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        aria-label={ariaLabel}
        onChange={handleTextareaChange}
        onKeyDown={handleTextareaKeyDown}
        onClick={(event) => {
          handleSelectionCapture(event);
          if (typeof onClick === 'function') onClick(event);
        }}
        onFocus={(event) => {
          setIsFocused(true);
          handleSelectionCapture(event);
          if (typeof onFocus === 'function') onFocus(event);
        }}
        onSelect={(event) => {
          handleSelectionCapture(event);
          if (typeof onSelect === 'function') onSelect(event);
        }}
        onBlur={(event) => {
          const nextTarget = event?.relatedTarget || null;
          if (nextTarget && wrapperRef.current?.contains(nextTarget)) {
            return;
          }
          setIsFocused(false);
          setCreateFormOpen(false);
          if (typeof onBlur === 'function') onBlur(event);
        }}
      />
      {children}
      {autocompleteState.open ? (
        <div
          ref={menuRef}
          className="bom-autocomplete-menu"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`
          }}
          role="listbox"
          aria-label="BOM markdown autocomplete"
        >
          <div className="bom-autocomplete-title">
            {getAutocompleteTitle(autocompleteState.context)}
          </div>
          {autocompleteState.options.map((option, optionIndex) => (
            <button
              type="button"
              key={`${option.type}-${option.value}-${optionIndex}`}
              className={`bom-autocomplete-option${activeIndex === optionIndex ? ' is-active' : ''}`}
              onMouseDown={(event) => {
                event.preventDefault();
                if (autocompleteState.context?.stage === 'bom') {
                  selectBomOption(option);
                } else if (autocompleteState.context?.stage === 'guide') {
                  selectGuideOption(option);
                } else if (autocompleteState.context?.stage === 'bom-filter') {
                  selectBomFilterOption(option);
                } else {
                  selectItemOption(option);
                }
              }}
            >
              <span className="bom-autocomplete-label">{option.label}</span>
              {option.subtitle ? (
                <span className="bom-autocomplete-subtitle">{option.subtitle}</span>
              ) : null}
            </button>
          ))}
          {autocompleteState.canCreateItem ? (
            <div className="bom-autocomplete-create">
              {createFormOpen ? (
                <form className="bom-autocomplete-create-form" onSubmit={submitCreateForm}>
                  <label className="bom-autocomplete-create-label">
                    <span>Name</span>
                    <input
                      type="text"
                      className="bom-autocomplete-create-input"
                      value={createForm.name}
                      onChange={(event) => updateCreateFormField('name', event.target.value)}
                      maxLength={200}
                      disabled={createLoading}
                      autoFocus
                    />
                  </label>
                  <label className="bom-autocomplete-create-label">
                    <span>Description</span>
                    <textarea
                      className="bom-autocomplete-create-textarea"
                      rows={3}
                      value={createForm.description}
                      onChange={(event) => updateCreateFormField('description', event.target.value)}
                      disabled={createLoading}
                    />
                  </label>
                  <div className="bom-autocomplete-create-grid">
                    <label className="bom-autocomplete-create-label">
                      <span>Quantity</span>
                      <input
                        type="text"
                        className="bom-autocomplete-create-input"
                        value={createForm.quantity}
                        onChange={(event) => updateCreateFormField('quantity', event.target.value)}
                        maxLength={64}
                        disabled={createLoading}
                      />
                    </label>
                    <label className="bom-autocomplete-create-label">
                      <span>Category</span>
                      <input
                        type="text"
                        className="bom-autocomplete-create-input"
                        list={categoryDatalistId}
                        value={createForm.category}
                        onChange={(event) => updateCreateFormField('category', event.target.value)}
                        maxLength={80}
                        disabled={createLoading}
                      />
                    </label>
                    <label className="bom-autocomplete-create-label">
                      <span>Module</span>
                      <input
                        type="text"
                        className="bom-autocomplete-create-input"
                        list={moduleDatalistId}
                        value={createForm.module}
                        onChange={(event) => updateCreateFormField('module', event.target.value)}
                        maxLength={120}
                        disabled={createLoading}
                      />
                    </label>
                  </div>
                  <datalist id={categoryDatalistId}>
                    {createFormSuggestions.category.map((category) => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                  <datalist id={moduleDatalistId}>
                    {createFormSuggestions.module.map((moduleName) => (
                      <option key={moduleName} value={moduleName} />
                    ))}
                  </datalist>
                  {createError ? (
                    <div className="bom-autocomplete-create-error">{createError}</div>
                  ) : null}
                  <div className="bom-autocomplete-create-actions">
                    <button type="submit" className="bom-autocomplete-create-submit" disabled={createLoading}>
                      {createLoading ? 'Creating...' : 'Create item'}
                    </button>
                    <button type="button" className="bom-autocomplete-create-cancel" onClick={closeCreateForm} disabled={createLoading}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {autocompleteState.showCreateFromQuery ? (
                    <button
                      type="button"
                      className="bom-autocomplete-create-button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        openCreateForm(autocompleteState.context?.query || '');
                      }}
                    >
                      Create "{sanitizeText(autocompleteState.context?.query).trim()}"
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="bom-autocomplete-create-button secondary"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      openCreateForm('');
                    }}
                  >
                    New Item
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default BomMarkdownTextarea;
